import { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import "./AdminDashboard.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AdminDashboard() {
  const navigate = useNavigate();
  const { userInfo, logout, hasAccess, isAdmin, authFetch } = useContext(AuthContext);
  const [orderCount, setOrderCount] = useState(0);
  const [inquiryCount, setInquiryCount] = useState(0);
  const [contactEnquiryCount, setContactEnquiryCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const [permissions, setPermissions] = useState({
    isFullAdmin: false,
    hasProductsAccess: false,
    hasOrdersAccess: false,
    hasInquiriesAccess: false,
    hasVouchersAccess: false,
    hasContactEnquiriesAccess: false
  });

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const adminStatus = await isAdmin();
        const productsAccess = adminStatus || await hasAccess("products");
        const ordersAccess = adminStatus || await hasAccess("orders");
        const inquiriesAccess = adminStatus || await hasAccess("inquiries");
        const vouchersAccess = adminStatus || await hasAccess("vouchers");
        const contactEnquiriesAccess = adminStatus || await hasAccess("contactEnquiries");

        setPermissions({
          isFullAdmin: adminStatus,
          hasProductsAccess: productsAccess,
          hasOrdersAccess: ordersAccess,
          hasInquiriesAccess: inquiriesAccess,
          hasVouchersAccess: vouchersAccess,
          hasContactEnquiriesAccess: contactEnquiriesAccess
        });
      } catch (err) {
        // Silent fail (previously only 1 console.error line in this catch block, no other actions)
      }
    };

    checkPermissions();
  }, [isAdmin, hasAccess]);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!permissions.hasOrdersAccess) {
        return;
      }

      try {
        const response = await authFetch(
          `${API_BASE_URL}/api/Orders?status=${encodeURIComponent("Paid")}`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch orders");
        }
        const data = await response.json();

        setOrderCount(Array.isArray(data) ? data.length : 0);
      } catch (err) {
        setOrderCount(0);
      }
    };

    const fetchInquiries = async () => {
      if (!permissions.hasInquiriesAccess) {
        return;
      }

      try {
        const response = await authFetch(
          `${API_BASE_URL}/inquiries?status=${encodeURIComponent("New")}`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch inquiries");
        }
        const data = await response.json();

        setInquiryCount(Array.isArray(data) ? data.length : 0);
      } catch (err) {
        setInquiryCount(0);
      }
    };

    // The list endpoint already returns per-status counts, so ask for the smallest
    // possible page rather than pulling every enquiry just to count them.
    const fetchContactEnquiries = async () => {
      if (!permissions.hasContactEnquiriesAccess) {
        return;
      }

      try {
        const response = await authFetch(`${API_BASE_URL}/api/ContactEnquiries?page=1&pageSize=1`);
        if (!response.ok) {
          throw new Error("Failed to fetch contact enquiries");
        }
        const data = await response.json();
        setContactEnquiryCount(data?.counts?.new || 0);
      } catch (err) {
        setContactEnquiryCount(0);
      }
    };

    const fetchAllData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([fetchOrders(), fetchInquiries(), fetchContactEnquiries()]);
      } catch (err) {
        // Silent fail
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, [userInfo, permissions, authFetch]);

  return (
    <div className="admin-dashboard-container">
      <div className="admin-dashboard-header">
        <div className="admin-dashboard-header-left">
          <div className="admin-dashboard-logo-container">
            <img
              src="./company-logo.png"
              alt="Company Logo"
              className="admin-dashboard-company-logo"
            />
          </div>
          <h1 className="admin-dashboard-title">Admin Dashboard</h1>
        </div>
        <div className="admin-dashboard-header-right">
          <div className="admin-dashboard-user-info">
            <p className="admin-dashboard-username">
              {userInfo?.username || "Admin User"}
            </p>
            <p className="admin-dashboard-role">
              {userInfo?.role === "admin"
                ? "Administrator"
                : userInfo?.role === "warehouse"
                  ? "Warehouse Admin"
                  : userInfo?.role === "sales"
                    ? "Sales Admin"
                    : "Admin User"}
            </p>
          </div>
          <button className="admin-dashboard-logout-btn" onClick={handleLogout}>
            <i className="fas fa-sign-out-alt"></i> Logout
          </button>
        </div>
      </div>

      <div className="admin-dashboard-grid">
        {permissions.hasProductsAccess && (
          <div className="admin-dashboard-card">
            <i className="fas fa-box admin-dashboard-card-icon"></i>
            <h2 className="admin-dashboard-card-title">
              Products & Categories
            </h2>
            <p className="admin-dashboard-card-description">
              Manage product inventory, update prices, descriptions, and
              organize categories.
            </p>
            <Link to="/admin/products" className="admin-dashboard-card-btn">
              Manage
            </Link>
          </div>
        )}

        {permissions.hasProductsAccess && (
          <div className="admin-dashboard-card admin-dashboard-card-orange">
            <i className="fas fa-gift admin-dashboard-card-icon"></i>
            <h2 className="admin-dashboard-card-title">Redeemable Products</h2>
            <p className="admin-dashboard-card-description">
              Manage products available for points redemption.
            </p>
            <Link to="/admin/products/redeemable" className="admin-dashboard-card-btn">
              Manage
            </Link>
          </div>
        )}

        {permissions.hasOrdersAccess && (
          <div className="admin-dashboard-card admin-dashboard-card-blue">
            <i className="fas fa-shopping-cart admin-dashboard-card-icon"></i>
            <h2 className="admin-dashboard-card-title">Orders</h2>
            {isLoading ? (
              <div className="admin-dashboard-badge admin-dashboard-badge-loading">
                ...
              </div>
            ) : (
              <div className="admin-dashboard-badge">{orderCount}</div>
            )}
            <p className="admin-dashboard-card-description">
              View and process customer orders.
            </p>
            <Link to="/admin/orders" className="admin-dashboard-card-btn">
              Manage
            </Link>
          </div>
        )}

        {permissions.hasInquiriesAccess && (
          <div className="admin-dashboard-card admin-dashboard-card-purple">
            <i className="fas fa-question-circle admin-dashboard-card-icon"></i>
            <h2 className="admin-dashboard-card-title">Inquiries</h2>
            {isLoading ? (
              <div className="admin-dashboard-badge admin-dashboard-badge-loading">
                ...
              </div>
            ) : (
              <div className="admin-dashboard-badge">{inquiryCount}</div>
            )}
            <p className="admin-dashboard-card-description">
              Review and respond to customer inquiries.
            </p>
            <Link to="/admin/inquiries" className="admin-dashboard-card-btn">
              Manage
            </Link>
          </div>
        )}
      </div>

      <div className="admin-dashboard-grid">
        {permissions.hasVouchersAccess && (
          <div className="admin-dashboard-card admin-dashboard-card-yellow">
            <i className="fas fa-file-contract admin-dashboard-card-icon"></i>
            <h2 className="admin-dashboard-card-title">Vouchers</h2>
            <p className="admin-dashboard-card-description">
              Manage vouchers for customer usage.
            </p>
            <Link to="/admin/vouchers" className="admin-dashboard-card-btn">
              Manage
            </Link>
          </div>
        )}

        {permissions.hasVouchersAccess && (
          <div className="admin-dashboard-card admin-dashboard-card-indigo">
            <i className="fas fa-ticket-alt admin-dashboard-card-icon"></i>
            <h2 className="admin-dashboard-card-title">Redeemable Vouchers</h2>
            <p className="admin-dashboard-card-description">
              Manage vouchers available for customer redemption.
            </p>
            <Link to="/admin/vouchers/redeemable" className="admin-dashboard-card-btn">
              Manage
            </Link>
          </div>
        )}

        {permissions.isFullAdmin && (
          <div className="admin-dashboard-card admin-dashboard-card-red">
            <i className="fas fa-book admin-dashboard-card-icon"></i>
            <h2 className="admin-dashboard-card-title">Catalogues</h2>
            <p className="admin-dashboard-card-description">
              Manage product catalogues for customers to download.
            </p>
            <Link to="/admin/catalogue" className="admin-dashboard-card-btn">
              Manage
            </Link>
          </div>
        )}

        {permissions.isFullAdmin && (
          <div className="admin-dashboard-card admin-dashboard-card-brown">
            <i className="fas fa-images admin-dashboard-card-icon"></i>
            <h2 className="admin-dashboard-card-title">Homepage Images</h2>
            <p className="admin-dashboard-card-description">
              Manage advertisement slider and partner images shown on homepage.
            </p>
            <Link to="/admin/images" className="admin-dashboard-card-btn">
              Manage
            </Link>
          </div>
        )}

        {permissions.isFullAdmin && (
          <div className="admin-dashboard-card admin-dashboard-card-yellow">
            <i className="fas fa-image admin-dashboard-card-icon"></i>
            <h2 className="admin-dashboard-card-title">Homepage Pop-up</h2>
            <p className="admin-dashboard-card-description">
              Upload the pop-up image and set when it shows.
            </p>
            <Link to="/admin/homepage-popup" className="admin-dashboard-card-btn">
              Manage
            </Link>
          </div>
        )}

        {permissions.hasContactEnquiriesAccess && (
          <div className="admin-dashboard-card admin-dashboard-card-blue">
            <i className="fas fa-envelope-open-text admin-dashboard-card-icon"></i>
            <h2 className="admin-dashboard-card-title">Contact Enquiries</h2>
            {isLoading ? (
              <div className="admin-dashboard-badge admin-dashboard-badge-loading">
                ...
              </div>
            ) : (
              <div className="admin-dashboard-badge">{contactEnquiryCount}</div>
            )}
            <p className="admin-dashboard-card-description">
              View enquiries from the Contact Us page.
            </p>
            <Link to="/admin/contact-enquiries" className="admin-dashboard-card-btn">
              Manage
            </Link>
          </div>
        )}

          {permissions.isFullAdmin && (
          <div className="admin-dashboard-card admin-dashboard-card-blue">
            <i className="fas fa-shopping-cart admin-dashboard-card-icon"></i>
            <h2 className="admin-dashboard-card-title">Customer</h2>
            <p className="admin-dashboard-card-description">
              View customer accounts.
            </p>
            <Link to="/admin/customers" className="admin-dashboard-card-btn">
              View
            </Link>
          </div>
        )}
        {permissions.isFullAdmin && (
          <div className="admin-dashboard-card admin-dashboard-card-orange">
            <i className="fas fa-tags admin-dashboard-card-icon"></i>
            <h2 className="admin-dashboard-card-title">Promotion Products</h2>
            <p className="admin-dashboard-card-description">
              Manage products listed on the Promotion page with special sale prices.
            </p>
            <Link to="/admin/promotions" className="admin-dashboard-card-btn">
              Manage
            </Link>
          </div>
        )}
      </div>

      <div className="admin-dashboard-row-divider"></div>

      <div className="admin-dashboard-grid">
        {permissions.isFullAdmin && (
          <div className="admin-dashboard-card admin-dashboard-card-orange">
            <i className="fas fa-chart-line admin-dashboard-card-icon"></i>
            <h2 className="admin-dashboard-card-title">Sales Analytics</h2>
            <p className="admin-dashboard-card-description">
              View and generate sales reports and analytics.
            </p>
            <Link to="/admin/salesanalytics" className="admin-dashboard-card-btn">
              Manage
            </Link>
          </div>
        )}
        {permissions.isFullAdmin && (
          <div className="admin-dashboard-card admin-dashboard-card-yellow">
            <i className="fas fa-chart-line admin-dashboard-card-icon"></i>
            <h2 className="admin-dashboard-card-title">Customer Visit Analytics</h2>
            <p className="admin-dashboard-card-description">
              View Total Customer Visits.
            </p>
            <Link to="/admin/customervisit" className="admin-dashboard-card-btn">
              Manage
            </Link>
          </div>
        )} 
        {permissions.isFullAdmin && (
          <div className="admin-dashboard-card">
            <i className="fas fa-chart-line admin-dashboard-card-icon"></i>
            <h2 className="admin-dashboard-card-title">Product View Analytics</h2>
            <p className="admin-dashboard-card-description">
              See how many unique visitors viewed each product per day.
            </p>
            <Link to="/admin/productview" className="admin-dashboard-card-btn">
              Manage
            </Link>
          </div>
        )} 
        {permissions.isFullAdmin && (
          <div className="admin-dashboard-card admin-dashboard-card-purple">
            <i className="fas fa-chart-line admin-dashboard-card-icon"></i>
            <h2 className="admin-dashboard-card-title">Voucher Analytics</h2>
            <p className="admin-dashboard-card-description">
              View and generate voucher reports and analytics.
            </p>
            <Link to="/admin/voucheranalytics" className="admin-dashboard-card-btn">
              Manage
            </Link>
          </div>
        )}
        {permissions.isFullAdmin && (
          <div className="admin-dashboard-card admin-dashboard-card-brown">
            <i className="fas fa-users-cog admin-dashboard-card-icon"></i>
            <h2 className="admin-dashboard-card-title">User Reviews</h2>
            <p className="admin-dashboard-card-description">
              View and approve user reviews.
            </p>
            <Link
              to="/admin/review"
              className="admin-dashboard-card-btn"
            >
              Manage
            </Link>
          </div>
        )} 
        {permissions.isFullAdmin && (
          <div className="admin-dashboard-card admin-dashboard-card-blue">
            <i className="fas fa-users-cog admin-dashboard-card-icon"></i>
            <h2 className="admin-dashboard-card-title">Admin Users</h2>
            <p className="admin-dashboard-card-description">
              Manage different administrator accounts and permissions.
            </p>
            <Link
              to="/admin/manage-admins"
              className="admin-dashboard-card-btn"
            >
              Manage
            </Link>
          </div>
        )}
      </div>
      <div className="admin-dashboard-footer">
        Ardency Construction & Trading Sdn. Bhd.
      </div>
    </div>
  );
}

export default AdminDashboard;
